import { cancel, select, takeEvery } from 'redux-saga/effects';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  numberToGermanNumberFormattedString,
  Produktlinie,
} from '@ps20-frontend/frontend-models';
import {
  Feature,
  SpartenType,
  ThhHundDruckdaten,
  ThhHundVersichertesTierDruck,
  VorschaedenDruck,
} from '@ps20/bff-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { getProduktlinienDruck, mapGefahren, mapLinienpreis } from '@ps20-frontend/druckdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { selectSelectedProduktlinie } from '../../app/shared/selectors/produkt.selector';
import { IAppState } from '../appState';
import { Vorschaeden } from '../reducers/vorschaeden.reducer';
import { selectVorschaeden } from '../../app/shared/selectors/vorschaeden.selector';
import { AnzahlTiereEnum, Versicherungsnehmer } from '../reducers/versicherungsnehmer.reducer';

export function* watchDruckdatenChanged() {
  yield takeEvery(
    GlobalActions.DRUCKDATEN_REQUESTED,
    safeSaga(onDruckdatenRequested, App.THH_HUND),
  );
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const state: IAppState = yield select();
    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.THH_HUND)) {
      yield cancel();
      return;
    }

    const thhHundDruckdaten: ThhHundDruckdaten = mapDruckdaten(state);
    GlobalPut.getInstance().put(new GlobalActions().druckdatenGenerated(thhHundDruckdaten));
  } catch (e) {
    PS20Logger.error(App.THH_HUND, e as Error, 'Druckdatensaga.onDruckdatenRequested()');
    yield cancel();
  }
}

function getVersicherteTiereDataFromStrecke(
  versicherungsnehmer: Versicherungsnehmer,
): ThhHundVersichertesTierDruck[] {
  const anzahlTiereEnumToAnzahlMapper = {
    [AnzahlTiereEnum.EINES]: [versicherungsnehmer.rasseEinHund],
    [AnzahlTiereEnum.MEHRERE]: versicherungsnehmer.rasseMehrereHunde,
  };
  const hundeRassenSchluesselListe =
    anzahlTiereEnumToAnzahlMapper[versicherungsnehmer.anzahlTiere];
  return hundeRassenSchluesselListe.map(rasseDomaenenwertSchluessel => ({
    chipnummer: null,
    geburtsdatum: null,
    geschlecht: null,
    rasse: rasseDomaenenwertSchluessel,
    taetonummer: null,
    tiername: null,
  } as ThhHundVersichertesTierDruck));
}

function mapDruckdaten(state: IAppState): ThhHundDruckdaten {
  const vorschaeden: Vorschaeden = selectVorschaeden(state);
  const ausgewaehlteProduktlinie: Produktlinie = selectSelectedProduktlinie(state);

  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  return {
    zusatzoptionenDruck: [],
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie.langtext,
    shortText: ausgewaehlteProduktlinie.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    zaehlstuecke: state.rabattstufe,
    treuebonus: state.taasInfo.tbNachlass,
    spartenType: SpartenType.TIERHALTERHAFTPFLICHT_HUND,
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.THH_HUND,
      ),
    ),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    linienpreis: mapLinienpreis(state.produkt),
    leistungen: mapLeistungen(ausgewaehlteProduktlinie, SpartenType.TIERHALTERHAFTPFLICHT_HUND),
    vorschaedenDruck: mapVorschaeden(vorschaeden),
    gefahrenDruck: mapGefahren(state.produkt),
    produktlinien: getProduktlinienDruck(state.produkt),
    zuersGefaehrdungsklasse: '',
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    abweichungVomZielmarkt: false,
    versicherteTiere: getVersicherteTiereDataFromStrecke(state.versicherungsnehmer),
    ...druckdatenProvidedInCheckout,
    beibehalten: state.vertragsoptionen.beibehalten,
    nachlass: ausgewaehlteProduktlinie.nachlass,
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
  };
}

const druckdatenProvidedInCheckout: Pick<ThhHundDruckdaten, 'meldebehoerde' | 'vorversicherungenDruck' | 'vertragsdauer' | 'beginn' | 'ablauf'> = {
  meldebehoerde: null,
  vorversicherungenDruck: null,
  vertragsdauer: null,
  beginn: null,
  ablauf: null,
};

function mapVorschaeden(vorschaden: Vorschaeden): VorschaedenDruck {
  return {
    vorhanden: true,
    haftpflicht: vorschaden.thhHundVorschaeden,
  };
}
